import { z } from 'zod'

/**
 * Live meeting copilot (ADR 0006): the client captures audio, the server transcribes (Deepgram), keeps the
 * transcript and streams copilot tips that draw on the project's knowledge. Paths below /api/v1 unless noted.
 */

const id = z.uuid()

export const LiveMeetingType = z.enum([
  'sales',
  'interview_candidate',
  'interview_interviewer',
  'technical',
  'general',
])
export type LiveMeetingType = z.infer<typeof LiveMeetingType>

export const LiveLanguage = z.enum(['auto', 'de', 'en'])
export type LiveLanguage = z.infer<typeof LiveLanguage>

/** mic = the user ("Ich"), system = everybody else in the call ("Gegenüber") */
export const AudioChannel = z.enum(['mic', 'system'])
export type AudioChannel = z.infer<typeof AudioChannel>

// POST /live/sessions → LiveSessionCreated
/** screen frames per second of a recording (ADR 0020): few frames keep the file small */
export const RecordingFps = z.union([
  z.literal(0.2),
  z.literal(0.5),
  z.literal(1),
  z.literal(2),
  z.literal(5),
])
export type RecordingFps = z.infer<typeof RecordingFps>
export const RECORDING_FPS: readonly RecordingFps[] = [0.2, 0.5, 1, 2, 5]

/** record the meeting (ADR 0020): audio of both channels, optionally the screen with few frames per second */
export const RecordingOptions = z.object({
  video: z.boolean().default(false),
  fps: RecordingFps.default(0.5),
})
export type RecordingOptions = z.infer<typeof RecordingOptions>

/** 'single' = 1:1 call, every remote voice is "Gegenüber 1"; 'auto' = diarization decides */
export const RemoteSpeakers = z.enum(['auto', 'single'])
export type RemoteSpeakers = z.infer<typeof RemoteSpeakers>

export const StartLiveSession = z.object({
  type: LiveMeetingType,
  title: z.string().max(200).default(''),
  /** free text for the copilot (job description, goals, …) – project knowledge is added automatically */
  context: z.string().max(20_000).default(''),
  language: LiveLanguage.default('auto'),
  containerId: id.nullable().default(null),
  /** whether the co-pilot may be switched on at all (false = transcript only; also forced when the container's
   * AI policy forbids external AI). It always starts switched OFF; the user turns it on with the slider
   * (WS message `copilot`). While off, no LLM calls are made (no co-pilot hints, no running notes). */
  copilot: z.boolean().default(true),
  /** started from a calendar event on "Heute" (ADR 0016): links the meeting to the event (provider event id) */
  calendarEventId: z.string().max(500).nullable().default(null),
  /** invited attendees – become the meeting's participants (together with named speakers) */
  attendees: z
    .array(z.object({ name: z.string().max(200), email: z.string().max(320).nullable().default(null) }))
    .max(100)
    .default([]),
  /** null = no recording */
  recording: RecordingOptions.nullable().default(null),
})
export type StartLiveSession = z.infer<typeof StartLiveSession>

export const LiveSessionCreated = z.object({
  meetingId: id,
  /** relative WebSocket URL incl. a short-lived ticket, e.g. /api/v1/live/ws?ticket=… (valid 60 s, reusable
   * for reconnects of the same meeting via POST /live/sessions/:meetingId/ticket) */
  wsUrl: z.string(),
  copilot: z.boolean(),
  /** why the copilot is off, German, e.g. "KI ist für dieses Projekt deaktiviert." */
  copilotReason: z.string().nullable(),
  /** number of project items the copilot knows (for the UI: "kennt 34 Notizen, 12 Tasks …") */
  knowledge: z.object({
    notes: z.number().int(),
    tasks: z.number().int(),
    decisions: z.number().int(),
    commitments: z.number().int(),
    people: z.number().int(),
    meetings: z.number().int(),
    /** project files with extracted text (ADR 0017) */
    files: z.number().int().optional(),
    /** documents of connected Git sources (ADR 0019) */
    sources: z.number().int().optional(),
  }),
})
export type LiveSessionCreated = z.infer<typeof LiveSessionCreated>

// POST /live/sessions/:meetingId/ticket → { wsUrl } (reconnect / resume after a reload or server restart)
// POST /live/sessions/:meetingId/stop → { meetingId } (same as the WS "stop" message; idempotent)
// GET  /live/sessions/active → { sessions: { meetingId, title, startedAt, containerId }[] } (resume banner)

export const TranscriptSegmentLive = z.object({
  id: z.string(),
  speaker: z.enum(['me', 'them']),
  /** diarized index on the system channel: "Gegenüber 1", "Gegenüber 2" … */
  speakerIndex: z.number().int().optional(),
  text: z.string(),
  /** milliseconds since meeting start */
  startMs: z.number(),
  endMs: z.number(),
  language: z.string().optional(),
})
export type TranscriptSegmentLive = z.infer<typeof TranscriptSegmentLive>

export const TipKind = z.enum(['question', 'objection_response', 'answer_suggestion', 'warning', 'info'])
export type TipKind = z.infer<typeof TipKind>

export const Tip = z.object({
  id: z.string(),
  kind: TipKind,
  /** max. 8 words */
  headline: z.string(),
  /** bullet points the user can say directly (first person) */
  say: z.array(z.string()),
  /** 0-3 small hints, not to be read out */
  points: z.array(z.string()),
  /** project items the tip relies on, e.g. a task or note title – shown as small chips */
  sources: z.array(
    z.object({
      type: z.enum(['note', 'task', 'decision', 'commitment', 'meeting', 'person', 'file', 'source']),
      id,
      title: z.string(),
    }),
  ),
  createdAtMs: z.number(),
  latencyMs: z.number().optional(),
  model: z.string(),
  trigger: z.enum(['auto', 'manual']),
  question: z.string().optional(),
  /** false while streaming; the same id is re-sent with growing content */
  done: z.boolean(),
})
export type Tip = z.infer<typeof Tip>

export const CopilotStatus = z.discriminatedUnion('state', [
  z.object({ state: z.literal('listening') }),
  z.object({ state: z.literal('thinking'), trigger: z.enum(['auto', 'manual']) }),
  z.object({ state: z.literal('skipped'), atMs: z.number() }),
  /** not available (AI policy, no provider) – reason German; the slider is disabled */
  z.object({ state: z.literal('off'), reason: z.string() }),
  /** available but switched off by the user (slider) */
  z.object({ state: z.literal('paused') }),
])
export type CopilotStatus = z.infer<typeof CopilotStatus>

/** key 'me' | 'them' | 'them:<speakerIndex>' → display name (optionally a linked person) */
export const LiveSpeakerNames = z.record(
  z.string(),
  z.object({ name: z.string(), personId: z.uuid().nullable().optional() }),
)
export type LiveSpeakerNames = z.infer<typeof LiveSpeakerNames>

export const ConnectionState = z.enum(['connecting', 'open', 'reconnecting', 'closed', 'error'])

/**
 * WebSocket protocol.
 * Client → server:
 *   binary frame: 1 byte channel (0 = mic, 1 = system) + PCM16 LE mono 16 kHz, ~100 ms per frame
 *   binary frame: 1 byte 2 + uint32 BE milliseconds since meeting start + JPEG – a screen frame of a recording
 *   text frame:   LiveClientMessage (JSON)
 * Server → client: text frames with LiveServerEvent (JSON). On (re)connect the server first sends `snapshot`.
 */
export const LiveClientMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ask'), question: z.string().max(2000).optional() }),
  /** co-pilot slider: on = automatic hints + running notes; off = transcript only. `ask` also works only when on. */
  z.object({ type: z.literal('copilot'), enabled: z.boolean() }),
  /** the user's own notes ("Meine Notizen"), full text, debounced; last write wins */
  z.object({ type: z.literal('notes'), text: z.string().max(200_000) }),
  /** rename a speaker: key 'me' | 'them' | 'them:<speakerIndex>'; name null/empty resets to the default label */
  z.object({
    type: z.literal('speaker'),
    key: z.string().max(50),
    name: z.string().trim().max(200).nullable(),
    personId: z.uuid().nullable().optional(),
  }),
  /** correct a final segment (misheard term): the changed words become a rule for the rest of the meeting */
  z.object({
    type: z.literal('segment-edit'),
    id: z.string().max(20),
    text: z.string().trim().min(1).max(5000),
  }),
  /** 1:1 call ('single': every remote voice is one person) or several remote speakers ('auto') */
  z.object({ type: z.literal('remote-speakers'), mode: RemoteSpeakers }),
  /** the client tells which sources it actually captures (no system audio in Safari/iOS) */
  z.object({ type: z.literal('sources'), mic: z.boolean(), system: z.boolean() }),
  z.object({ type: z.literal('stop') }),
  z.object({ type: z.literal('ping') }),
])
export type LiveClientMessage = z.infer<typeof LiveClientMessage>

export const LiveServerEvent = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('snapshot'),
    meetingId: id,
    title: z.string(),
    startedAt: z.iso.datetime({ offset: true }),
    segments: z.array(TranscriptSegmentLive),
    tips: z.array(Tip),
    status: CopilotStatus,
    /** current slider state (false right after start) */
    copilotEnabled: z.boolean(),
    userNotes: z.string(),
    speakerNames: LiveSpeakerNames,
    /** diarization mode of the other side (default 'auto'; 'single' when the invite has one other attendee) */
    remoteSpeakers: RemoteSpeakers.optional(),
    /** the meeting is being recorded (the client then sends screen frames when `video` is set) */
    recording: RecordingOptions.nullable().optional(),
  }),
  z.object({ type: z.literal('segment'), segment: TranscriptSegmentLive }),
  z.object({ type: z.literal('segment-removed'), id: z.string() }),
  /** segments changed by a correction (the edited one and others containing the same term) */
  z.object({
    type: z.literal('segments-updated'),
    segments: z.array(TranscriptSegmentLive),
    /** the rule that was derived, if any */
    correction: z.object({ from: z.string(), to: z.string() }).nullable(),
  }),
  z.object({
    type: z.literal('interim'),
    speaker: z.enum(['me', 'them']),
    text: z.string(),
  }),
  z.object({ type: z.literal('tip'), tip: Tip }),
  z.object({ type: z.literal('copilot-status'), status: CopilotStatus }),
  /** echo of the slider state (also sent to other connected tabs/devices of the same meeting) */
  z.object({ type: z.literal('copilot-enabled'), enabled: z.boolean() }),
  /** the user's notes changed on another device/tab */
  z.object({ type: z.literal('user-notes'), text: z.string() }),
  z.object({ type: z.literal('speaker-names'), names: LiveSpeakerNames }),
  z.object({
    type: z.literal('connection'),
    channel: AudioChannel,
    state: ConnectionState,
    message: z.string().optional(),
  }),
  z.object({ type: z.literal('error'), message: z.string() }),
  /** meeting stopped and handed to meeting.process; client navigates to /meetings/:meetingId */
  z.object({ type: z.literal('ended'), meetingId: id }),
  z.object({ type: z.literal('pong') }),
])
export type LiveServerEvent = z.infer<typeof LiveServerEvent>

// GET /meetings/:id/tips → { tips: Tip[], notes: string } (tips given during a live meeting + running notes)

/** Copilot settings, part of GET/PATCH /settings (field `copilot`). */
export const CopilotSettings = z.object({
  liveModel: z.enum(['claude-haiku-5-5', 'claude-sonnet-5-5']).default('claude-haiku-5-5'),
  defaultLanguage: LiveLanguage.default('auto'),
  /** minimum distance between two automatic tips */
  tipCooldownSec: z.number().int().min(2).max(120).default(6),
  /** minimum words from the other side before an automatic tip */
  tipMinWords: z.number().int().min(2).max(100).default(8),
  defaultType: LiveMeetingType.default('general'),
  /** defaults of the start dialog for recordings (ADR 0020) */
  recordByDefault: z.boolean().default(false),
  recordVideo: z.boolean().default(false),
  videoFps: RecordingFps.default(0.5),
  /** glossary: technical terms and names the transcription should recognize (Deepgram keyterms); corrected
   * terms are added automatically */
  keyterms: z.array(z.string().trim().min(1).max(60)).max(100).default([]),
})
export type CopilotSettings = z.infer<typeof CopilotSettings>

/** GET /meetings/:id/recording (ADR 0020) */
export const MeetingRecording = z.object({
  meetingId: id,
  /** recording = meeting still live; processing = encoding and speaker recognition after the call */
  status: z.enum(['recording', 'processing', 'ready', 'failed']),
  hasVideo: z.boolean(),
  videoFps: z.number().nullable(),
  mime: z.string().nullable(),
  size: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  /** speakers of the other side were re-recognized from the recording after the call */
  rediarized: z.boolean(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  readyAt: z.iso.datetime({ offset: true }).nullable(),
  /** relative URL of the media file (supports HTTP Range for seeking) */
  mediaUrl: z.string().nullable(),
})
export type MeetingRecording = z.infer<typeof MeetingRecording>
